import { h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import Popover, { PopoverMode, PopoverProps } from "../Popover";

// Delays
const SHOW_DELAY_DEFAULT = 500; // ms
const HIDE_DELAY_DEFAULT = 200; // ms

type InheritedPopoverProps = Omit<PopoverProps, "open" | "mode" | "portalOptions">;

interface Props extends InheritedPopoverProps {
    targetClass?: string;
    portalInto?: HTMLElement;
    showDelay?: number;
    hideDelay?: number;
    hideOnClick?: boolean;
    onVisibilityChange?: (open: boolean) => void;
}

function PopoverWithHover({
    targetClass,
    portalInto,
    content,
    showDelay = SHOW_DELAY_DEFAULT,
    hideDelay = HIDE_DELAY_DEFAULT,
    hideOnClick = true,
    scrollContainerOptions: {
        ref: scrollContainerRef,
        ...scrollContainerOptions
    } = {},
    children,
    onVisibilityChange,
    ...popoverProps
}: Props) {
    const [open, setOpen] = useState(false);

    const targetRef = useRef<HTMLDivElement>();
    const showTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    function handleVisibilityChange(isVisible: boolean) {
        setOpen(isVisible);
        onVisibilityChange?.(isVisible);
    }

    function handleTargetMouseEnter() {
        clearTimeout(hideTimeoutRef.current);
        clearTimeout(showTimeoutRef.current);

        if (!open) {
            showTimeoutRef.current = setTimeout(() => {
                handleVisibilityChange(true);
            }, showDelay);
        }
    }

    function handleContentMouseEnter() {
        clearTimeout(hideTimeoutRef.current);
    }

    function handleMouseLeave() {
        clearTimeout(hideTimeoutRef.current);
        clearTimeout(showTimeoutRef.current);

        hideTimeoutRef.current = setTimeout(() => {
            handleVisibilityChange(false);
        }, hideDelay);
    }

    function handleTargetClick() {
        clearTimeout(hideTimeoutRef.current);

        handleVisibilityChange(false);
    }

    useEffect(() => {
        if (!scrollContainerRef) {
            return;
        }

        function handleScroll() {
            clearTimeout(showTimeoutRef.current);
            clearTimeout(hideTimeoutRef.current);

            handleVisibilityChange(false);
        }

        const subscribe = () => scrollContainerRef.current?.addEventListener("scroll", handleScroll);
        const unsubscribe = () => scrollContainerRef.current?.removeEventListener("scroll", handleScroll);

        if (open) {
            subscribe();
        } else {
            unsubscribe();
        }

        return () => {
            unsubscribe();
        };
    }, [open, scrollContainerRef]);

    function renderContent() {
        return (
            <div onMouseEnter={handleContentMouseEnter} onMouseLeave={handleMouseLeave}>
                {content}
            </div>
        );
    }

    return (
        <div ref={targetRef}
            class={targetClass}
            onClick={hideOnClick ? handleTargetClick : undefined}
            onMouseEnter={handleTargetMouseEnter}
            onMouseLeave={handleMouseLeave}>
            {children}
            <Popover
                {...popoverProps}
                open={open}
                mode={PopoverMode.Portal}
                content={renderContent()}
                portalOptions={{
                    targetRef,
                    portalInto
                }}
                scrollContainerOptions={{
                    ref: scrollContainerRef,
                    ...scrollContainerOptions
                }}/>
        </div>
    );
}

export default PopoverWithHover;
export {
    Props as PopoverWithHoverProps
};
